import * as React from 'react';
import { cn } from '../../utils/cn';
import type { IconButtonProps } from './icon-button.types';

/**
 * IconButton Component
 *
 * A button component that displays only an icon.
 * Perfect for toolbar actions, navigation, and compact interfaces.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            icon,
            size = 'medium',
            onPress,
            disabled = false,
            variant = 'default',
            iconColor,
            containerClassName,
            testID,
            ariaLabel,
            onClick,
            ...props
        },
        ref
    ) => {
        const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            if (disabled) return;
            onPress?.();
            onClick?.(e);
        };

        const sizeClasses = {
            small: 'w-8 h-8',
            medium: 'w-10 h-10',
            large: 'w-12 h-12',
        };

        const iconSizeClasses = {
            small: 'w-4 h-4',
            medium: 'w-5 h-5',
            large: 'w-6 h-6',
        };

        const variantClasses = {
            default: 'hover:bg-[var(--color-neutral-10)] active:bg-[var(--color-neutral-20)]',
            contained:
                'bg-[var(--color-info-60)] text-[var(--color-neutral-0)] hover:bg-[var(--color-info-70)] active:bg-[var(--color-info-80)]',
            outlined:
                'border-2 border-[var(--color-info-60)] text-[var(--color-info-60)] hover:bg-[var(--color-info-5)] active:bg-[var(--color-info-10)]',
            text: 'text-[var(--color-info-60)] hover:bg-[var(--color-info-5)] active:bg-[var(--color-info-10)]',
        };

        return (
            <button
                ref={ref}
                type="button"
                onClick={handleClick}
                disabled={disabled}
                data-testid={testID || 'icon-button'}
                aria-label={ariaLabel}
                className={cn(
                    'inline-flex items-center justify-center rounded-full',
                    'transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-[var(--color-info-50)] focus:ring-offset-2',
                    sizeClasses[size],
                    variantClasses[variant],
                    disabled && 'opacity-50 cursor-not-allowed',
                    !disabled && 'cursor-pointer active:scale-95',
                    containerClassName
                )}
                {...props}
            >
                <div className={cn(iconSizeClasses[size])} style={iconColor ? { color: iconColor } : undefined}>
                    {icon}
                </div>
            </button>
        );
    }
);

IconButton.displayName = 'IconButton';
